import {
  ELIGIBILITY_STATUS,
  EPL_LOCAL_DATA,
  MODEL_MAP,
  ORIGIN_MAP,
} from "data/constants"
import {
  getActiveAccount,
  isUserEligibleForEbayMarketing,
} from "shared/helpers/eligibility"
import { DataStore } from "shared/helpers/localStorage"

const CUSTOMER_STATUS = {
  NOT_STARTED: "not_started",
  PAYING: "paying",
  REGULAR: "regular",
}

const SELLER_TYPE = {
  ALL: "all",
  DOMESTIC: "domestic",
  INTERNATIONAL: "international",
}

export const getCustomerStatusByModelAndOrigin = (statuses, model, origin) =>
  statuses.find(el => el.funding_model === model && el.origin === origin)
    ?.status

export const canSellerSignUp = entitlements =>
  entitlements?.data.can_onboard_to_epl ||
  entitlements?.data.can_onboard_to_epl_domestic ||
  entitlements?.data.can_onboard_to_epla ||
  entitlements?.data.can_onboard_to_epla_domestic

export const getPLSDomesticCustomerStatus = statuses =>
  getCustomerStatusByModelAndOrigin(
    statuses || [],
    MODEL_MAP.CPS,
    ORIGIN_MAP.CUSTOMER
  )

export const getPLSInternationalCustomerStatus = statuses =>
  getCustomerStatusByModelAndOrigin(
    statuses || [],
    MODEL_MAP.CPS,
    ORIGIN_MAP.WI
  )

export const getPLADomesticCustomerStatus = statuses =>
  getCustomerStatusByModelAndOrigin(
    statuses || [],
    MODEL_MAP.CPC,
    ORIGIN_MAP.CUSTOMER
  )

export const getPLAInternationalCustomerStatus = statuses =>
  getCustomerStatusByModelAndOrigin(
    statuses || [],
    MODEL_MAP.CPC,
    ORIGIN_MAP.WI
  )

export const isNotEPL = eplOnboarding => {
  const statuses = eplOnboarding?.data?.statuses
  if (!statuses || statuses?.length === 0) return true
  return statuses.filter(el => el.status === "not_started").length === 4
}

export const isOnboardedToEPL = eplOnboarding => {
  const statuses = eplOnboarding?.data?.statuses
  if (!statuses || statuses?.length === 0) return false
  return (
    getPLSDomesticCustomerStatus(statuses) !== undefined ||
    getPLSInternationalCustomerStatus(statuses) !== undefined ||
    getPLADomesticCustomerStatus(statuses) !== undefined ||
    getPLAInternationalCustomerStatus(statuses) !== undefined
  )
}

export const showEplMiniDashboard = (entitlements, eplOnboarding) =>
  canSellerSignUp(entitlements) && getPLSSellerType(eplOnboarding) !== undefined

export const planSelectionStatus = eplOnboarding => {
  const statuses = eplOnboarding?.data?.statuses
  if (!statuses || statuses?.length === 0) return false
  return (
    ![CUSTOMER_STATUS.NOT_STARTED, undefined].includes(
      getPLSDomesticCustomerStatus(statuses)
    ) ||
    ![CUSTOMER_STATUS.NOT_STARTED, undefined].includes(
      getPLSInternationalCustomerStatus(statuses)
    ) ||
    ![CUSTOMER_STATUS.NOT_STARTED, undefined].includes(
      getPLADomesticCustomerStatus(statuses)
    ) ||
    ![CUSTOMER_STATUS.NOT_STARTED, undefined].includes(
      getPLAInternationalCustomerStatus(statuses)
    )
  )
}

export const getEplEligibilityStatus = (accounts, activeAccount) => {
  const accountsArray = accounts?.data?.accounts
  if (!accountsArray) return ELIGIBILITY_STATUS.NOT_PROCESSED
  const account = getActiveAccount({ activeAccount, accounts: accountsArray })
  if (!account) return ELIGIBILITY_STATUS.NOT_PROCESSED
  return isUserEligibleForEbayMarketing(account)
}

export const getPLASellerType = eplOnboarding => {
  const statuses = eplOnboarding?.data?.statuses
  if (!statuses) return undefined
  const isDomesticSeller = [
    CUSTOMER_STATUS.REGULAR,
    CUSTOMER_STATUS.PAYING,
  ].includes(getPLADomesticCustomerStatus(statuses))
  const isInternationalSeller = [
    CUSTOMER_STATUS.REGULAR,
    CUSTOMER_STATUS.PAYING,
  ].includes(getPLAInternationalCustomerStatus(statuses))
  if (isDomesticSeller && isInternationalSeller) return SELLER_TYPE.ALL
  if (isDomesticSeller) return SELLER_TYPE.DOMESTIC
  if (isInternationalSeller) return SELLER_TYPE.INTERNATIONAL
  return undefined
}

export const getPLSSellerType = eplOnboarding => {
  const statuses = eplOnboarding?.data?.statuses
  if (!statuses) return undefined
  const isDomesticSeller = [
    CUSTOMER_STATUS.REGULAR,
    CUSTOMER_STATUS.PAYING,
  ].includes(getPLSDomesticCustomerStatus(statuses))
  const isInternationalSeller = [
    CUSTOMER_STATUS.REGULAR,
    CUSTOMER_STATUS.PAYING,
  ].includes(getPLSInternationalCustomerStatus(statuses))
  if (isDomesticSeller && isInternationalSeller) return SELLER_TYPE.ALL
  if (isDomesticSeller) return SELLER_TYPE.DOMESTIC
  if (isInternationalSeller) return SELLER_TYPE.INTERNATIONAL
  return undefined
}

export const getEiasToken = (userAccounts, activeAccount) => {
  const accounts = userAccounts?.data?.data?.accounts || []
  if (accounts.length === 0) return null
  return accounts.find(account => account.account_id === activeAccount)
    ?.eias_token
}

export const getCurrentEPLPlatformState = ({
  platformStates,
  model,
  origin,
}) => {
  const platform = `${model === "CPS" ? "epl" : "epla"}${origin === "customer" ? "-domestic" : ""
    }`
  const eplStates = (platformStates || []).filter(
    platformState => platformState.user_parameters.platform === platform
  )

  if (!eplStates.length) {
    return {}
  }

  const currentStateLetter = eplStates[0].user_parameters.current_state
  const matchingStates = eplStates.filter(
    eplState => eplState.state === currentStateLetter
  )
  const currentState = matchingStates.sort((firstState, secondState) => {
    const firstStateDate = new Date(firstState.start)
    const secondStateDate = new Date(secondState.start)
    return secondStateDate - firstStateDate
  })[0]

  return currentState || {}
}

const { EPL_LOCAL_STORAGE, SHOULD_SEE_EPL_RESULTS } = EPL_LOCAL_DATA

export const setShouldSeeEPLResult = () =>
  DataStore.set(EPL_LOCAL_STORAGE, { [SHOULD_SEE_EPL_RESULTS]: true })

export const unsetShouldSeeEPLResult = () =>
  DataStore.remove(EPL_LOCAL_STORAGE, SHOULD_SEE_EPL_RESULTS)

export default {
  canSellerSignUp,
  getPLSDomesticCustomerStatus,
  getPLSInternationalCustomerStatus,
  getPLADomesticCustomerStatus,
  getPLAInternationalCustomerStatus,
  showEplMiniDashboard,
  planSelectionStatus,
  getEplEligibilityStatus,
  getPLSSellerType,
  getPLASellerType,
  getEiasToken,
  setShouldSeeEPLResult,
  unsetShouldSeeEPLResult,
}
